import { useCallback, useEffect, useId, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { deleteRelease, listReleases, uploadRelease } from '@/api/releases'
import type { AgentReleaseSummary, AgentUpdateRolloutSummary } from '@/types'
import AgentRolloutModal from '@/components/AgentRolloutModal'
import { formatKstDateTime } from '@/utils/datetime'
import styles from './ReleasesPage.module.css'

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function formatTime(iso: string): string {
  return formatKstDateTime(iso, iso)
}

export default function ReleasesPage() {
  return <ReleasesPageInner />
}

function ReleasesPageInner() {
  const [items, setItems] = useState<AgentReleaseSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  // Upload form state
  const [showUpload, setShowUpload] = useState(false)
  const [version, setVersion] = useState('')
  const [channel, setChannel] = useState('stable')
  const [notes, setNotes] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState<string | null>(null)

  // Delete confirm
  const [pendingDelete, setPendingDelete] = useState<AgentReleaseSummary | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [pendingRollout, setPendingRollout] = useState<{ release: AgentReleaseSummary; rolloutId?: string } | null>(null)
  const [notesDetail, setNotesDetail] = useState<{
    release: AgentReleaseSummary
    trigger: HTMLButtonElement
  } | null>(null)

  const closeNotesDetail = useCallback(() => setNotesDetail(null), [])

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 3500)
  }

  const fetchReleases = useCallback(async () => {
    try {
      const res = await listReleases()
      setItems(res.items)
      setError(null)
    } catch (e: unknown) {
      const err = e as { message?: string }
      setError(err?.message ?? '릴리즈 목록을 불러올 수 없습니다.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    setLoading(true)
    fetchReleases()
  }, [fetchReleases])

  useEffect(() => {
    if (!items.some((item) => item.latest_rollout && !isRolloutFinal(item.latest_rollout.status))) {
      return
    }
    const id = window.setInterval(() => {
      fetchReleases()
    }, 5000)
    return () => window.clearInterval(id)
  }, [items, fetchReleases])

  function resetUploadForm() {
    setVersion('')
    setChannel('stable')
    setNotes('')
    setFile(null)
    setUploadError(null)
    setUploadProgress(0)
    setShowUpload(false)
  }

  function autoFillVersionFromFile(f: File) {
    // Filename convention: node-agent_<version>_<arch>.deb
    const m = f.name.match(/node-agent_([^_]+)_/)
    if (m && !version) setVersion(m[1])
  }

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault()
    if (!file) {
      setUploadError('파일을 선택하세요.')
      return
    }
    if (!version.trim()) {
      setUploadError('버전을 입력하세요.')
      return
    }
    setUploading(true)
    setUploadError(null)
    setUploadProgress(0)
    try {
      const created = await uploadRelease({
        version: version.trim(),
        channel,
        notes: notes.trim() || undefined,
        file,
        onProgress: (frac) => setUploadProgress(frac),
      })
      showToast(`업로드 완료: ${created.version} (${formatSize(created.size_bytes)})`)
      resetUploadForm()
      fetchReleases()
    } catch (err: unknown) {
      const e = err as { message?: string }
      setUploadError(e?.message ?? '업로드 실패')
    } finally {
      setUploading(false)
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await deleteRelease(pendingDelete.version)
      // Refresh list BEFORE closing the modal so the user can see the row
      // actually disappear (and we surface server-side fetch errors here too).
      await fetchReleases()
      showToast(`삭제 완료: ${pendingDelete.version}`)
      setPendingDelete(null)
    } catch (err: unknown) {
      const e = err as { message?: string }
      setDeleteError(e?.message ?? '삭제 실패')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>
          Agent Releases
        </h2>
        <button
          onClick={() => setShowUpload((v) => !v)}
          style={{
            padding: '8px 16px',
            background: showUpload ? '#999' : '#3949ab',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          {showUpload ? '취소' : '+ 새 릴리즈 업로드'}
        </button>
      </div>

      <p style={{ fontSize: 13, color: '#666', marginTop: 0, marginBottom: 16 }}>
        노드 자동 업데이트용 .deb 아티팩트. 버전은 immutable — 동일 버전 재업로드는 거부됩니다.
      </p>

      {toast && (
        <div style={{ padding: 10, background: '#e8f5e9', color: '#2e7d32', borderRadius: 4, marginBottom: 12, fontSize: 13 }}>
          {toast}
        </div>
      )}

      {error && (
        <div style={{ padding: 10, background: '#fdecea', color: '#c62828', borderRadius: 4, marginBottom: 12, fontSize: 13 }}>
          {error}
        </div>
      )}

      {showUpload && (
        <form
          onSubmit={handleUpload}
          style={{
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: 6,
            padding: 16,
            marginBottom: 16,
            display: 'grid',
            gap: 12,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#555' }}>
              버전 (SemVer)
              <input
                type="text"
                placeholder="0.2.1"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                disabled={uploading}
                style={{ width: '100%', padding: '6px 8px', marginTop: 4, border: '1px solid #ccc', borderRadius: 4, fontSize: 13 }}
              />
            </label>
            <label style={{ display: 'block', fontSize: 12, color: '#555' }}>
              채널
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                disabled={uploading}
                style={{ width: '100%', padding: '6px 8px', marginTop: 4, border: '1px solid #ccc', borderRadius: 4, fontSize: 13 }}
              >
                <option value="stable">stable</option>
                <option value="canary">canary</option>
              </select>
            </label>
          </div>

          <label style={{ display: 'block', fontSize: 12, color: '#555' }}>
            릴리즈 노트 (선택)
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={uploading}
              rows={2}
              style={{ width: '100%', padding: '6px 8px', marginTop: 4, border: '1px solid #ccc', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </label>

          <label style={{ display: 'block', fontSize: 12, color: '#555' }}>
            .deb 파일
            <input
              type="file"
              accept=".deb,application/vnd.debian.binary-package"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null
                setFile(f)
                if (f) autoFillVersionFromFile(f)
              }}
              disabled={uploading}
              style={{ display: 'block', marginTop: 4, fontSize: 13 }}
            />
            {file && (
              <span style={{ fontSize: 11, color: '#888', marginTop: 4, display: 'inline-block' }}>
                {file.name} · {formatSize(file.size)}
              </span>
            )}
          </label>

          {uploading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, height: 6, background: '#eee', borderRadius: 3, overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${Math.round(uploadProgress * 100)}%`,
                    height: '100%',
                    background: '#3949ab',
                    transition: 'width 0.1s',
                  }}
                />
              </div>
              <span style={{ fontSize: 11, color: '#666', width: 38, textAlign: 'right' }}>
                {Math.round(uploadProgress * 100)}%
              </span>
            </div>
          )}

          {uploadError && (
            <div style={{ padding: 8, background: '#fdecea', color: '#c62828', borderRadius: 4, fontSize: 12 }}>
              {uploadError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={resetUploadForm}
              disabled={uploading}
              style={{ padding: '6px 14px', background: '#f0f0f0', border: '1px solid #ccc', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}
            >
              취소
            </button>
            <button
              type="submit"
              disabled={uploading || !file || !version.trim()}
              style={{
                padding: '6px 14px',
                background: uploading ? '#999' : '#3949ab',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                fontSize: 13,
                cursor: uploading ? 'not-allowed' : 'pointer',
              }}
            >
              {uploading ? '업로드 중…' : '업로드'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div style={{ padding: 24, color: '#888' }}>로딩 중…</div>
      ) : items.length === 0 ? (
        <div
          style={{
            padding: 32,
            background: '#fff',
            border: '1px solid #eee',
            borderRadius: 6,
            color: '#888',
            textAlign: 'center',
          }}
        >
          등록된 릴리즈가 없습니다. 위 버튼으로 첫 .deb 를 업로드하세요.
        </div>
      ) : (
        <table
          style={{
            width: '100%',
            background: '#fff',
            border: '1px solid #ddd',
            borderCollapse: 'collapse',
            fontSize: 13,
          }}
        >
          <thead>
            <tr style={{ background: '#f5f5f7' }}>
              <th style={th}>버전</th>
              <th style={th}>채널</th>
              <th style={th}>파일</th>
              <th style={th}>크기</th>
              <th style={th}>SHA256</th>
              <th style={th}>업로드 시각</th>
              <th style={th}>배포 현황</th>
              <th style={th}>노트</th>
              <th style={{ ...th, width: 150 }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.version} style={{ borderTop: '1px solid #eee' }}>
                <td style={td}>
                  <strong>{r.version}</strong>
                </td>
                <td style={td}>
                  <span style={pill(r.channel === 'canary' ? '#fff3e0' : '#e3f2fd')}>{r.channel}</span>
                </td>
                <td style={{ ...td, fontFamily: 'ui-monospace', fontSize: 11 }}>{r.file_name}</td>
                <td style={td}>{formatSize(r.size_bytes)}</td>
                <td style={{ ...td, fontFamily: 'ui-monospace', fontSize: 11, color: '#666' }} title={r.sha256}>
                  {r.sha256.slice(0, 12)}…
                </td>
                <td style={td}>{formatTime(r.uploaded_at)}</td>
                <td style={{ ...td, minWidth: 170 }}>
                  <RolloutStatusCell
                    rollout={r.latest_rollout}
                    onOpen={
                      r.latest_rollout
                        ? () => setPendingRollout({ release: r, rolloutId: r.latest_rollout!.id })
                        : undefined
                    }
                  />
                </td>
                <td style={{ ...td, color: '#666', maxWidth: 260 }}>
                  <NotesPreview
                    release={r}
                    onOpen={(trigger) => setNotesDetail({ release: r, trigger })}
                  />
                </td>
                <td style={{ ...td, display: 'flex', gap: 6 }}>
                  <button
                    onClick={() => setPendingRollout({ release: r })}
                    style={{
                      padding: '4px 10px',
                      background: '#3949ab',
                      border: '1px solid #3949ab',
                      color: '#fff',
                      borderRadius: 4,
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    Deploy
                  </button>
                  <button
                    onClick={() => setPendingDelete(r)}
                    style={{
                      padding: '4px 10px',
                      background: '#fff',
                      border: '1px solid #c62828',
                      color: '#c62828',
                      borderRadius: 4,
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pendingDelete && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
          onClick={() => !deleting && (setPendingDelete(null), setDeleteError(null))}
        >
          <div
            style={{ background: '#fff', borderRadius: 8, padding: 24, maxWidth: 440, width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: 0, marginBottom: 12, fontSize: 16 }}>릴리즈 삭제</h3>
            <p style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
              버전 <code style={{ background: '#f5f5f7', padding: '1px 6px', borderRadius: 3 }}>{pendingDelete.version}</code>{' '}
              의 .deb 파일을 디스크와 DB 에서 모두 삭제합니다. 되돌릴 수 없습니다.
              <br />
              <br />
              현재 이 버전으로 진행 중인 업데이트가 있다면 노드가 다운로드에 실패할 수 있습니다.
            </p>
            {deleteError && (
              <div style={{ marginTop: 12, padding: 10, background: '#fdecea', color: '#c62828', borderRadius: 4, fontSize: 12 }}>
                {deleteError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                onClick={() => { setPendingDelete(null); setDeleteError(null) }}
                disabled={deleting}
                style={{ padding: '6px 14px', background: '#f0f0f0', border: '1px solid #ccc', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}
              >
                취소
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                style={{
                  padding: '6px 14px',
                  background: deleting ? '#999' : '#c62828',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: 13,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                }}
              >
                {deleting ? '삭제 중…' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingRollout && (
        <AgentRolloutModal
          release={pendingRollout.release}
          rolloutId={pendingRollout.rolloutId}
          onClose={() => setPendingRollout(null)}
          onToast={showToast}
          onRolloutUpdated={fetchReleases}
        />
      )}

      {notesDetail && (
        <NotesModal
          release={notesDetail.release}
          returnFocusTo={notesDetail.trigger}
          onClose={closeNotesDetail}
        />
      )}
    </div>
  )
}

function NotesPreview({
  release,
  onOpen,
}: {
  release: AgentReleaseSummary
  onOpen: (trigger: HTMLButtonElement) => void
}) {
  const previewId = useId()

  if (!release.notes) return <span>—</span>
  return (
    <div className={styles.notesPreview}>
      <div
        id={previewId}
        className={styles.notesPreviewContent}
        data-testid={`release-notes-preview-${release.version}`}
        style={{
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 3,
          overflow: 'hidden',
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          skipHtml
          components={{
            a: ({ children }) => <span>{children}</span>,
            img: ({ alt }) => <span>{alt}</span>,
          }}
        >
          {release.notes}
        </ReactMarkdown>
      </div>
      <button
        type="button"
        className={styles.notesPreviewButton}
        onClick={(event) => onOpen(event.currentTarget)}
        aria-label={`Agent ${release.version} 릴리즈 노트 보기`}
        aria-describedby={previewId}
        title="릴리즈 노트 보기"
      />
    </div>
  )
}

function NotesModal({
  release,
  returnFocusTo,
  onClose,
}: {
  release: AgentReleaseSummary
  returnFocusTo: HTMLButtonElement
  onClose: () => void
}) {
  const titleId = useId()
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', handleKeyDown)
    closeButtonRef.current?.focus()

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (returnFocusTo.isConnected) returnFocusTo.focus()
    }
  }, [onClose, returnFocusTo])

  return (
    <div
      className={styles.notesModalOverlay}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={styles.notesModalPanel}
      >
        <div className={styles.notesModalHeader}>
          <h3 id={titleId} style={{ margin: 0, fontSize: 16 }}>
            Agent {release.version} release notes
          </h3>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            style={secondaryBtnStyle}
          >
            닫기
          </button>
        </div>
        <div
          className={styles.notesMarkdownBody}
          data-testid="release-notes-body"
          style={{ minHeight: 0, overflow: 'auto' }}
        >
          {release.notes ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              skipHtml
              components={{
                a: ({ href, children }) => (
                  <a
                    href={safeLinkHref(href)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {children}
                  </a>
                ),
                img: ({ alt }) => <span>{alt}</span>,
              }}
            >
              {release.notes}
            </ReactMarkdown>
          ) : '—'}
        </div>
      </div>
    </div>
  )
}

function safeLinkHref(href: string | undefined): string | undefined {
  if (!href) return undefined
  const protocol = href.match(/^([a-z][a-z\d+.-]*):/i)?.[1].toLowerCase()
  return !protocol || ['http', 'https', 'mailto'].includes(protocol) ? href : undefined
}

function RolloutStatusCell({
  rollout,
  onOpen,
}: {
  rollout: AgentUpdateRolloutSummary | null
  onOpen?: () => void
}) {
  if (!rollout) {
    return <span style={{ fontSize: 12, color: '#999' }}>배포 없음</span>
  }

  const succeeded = rollout.counts_by_status.succeeded ?? 0
  const failed =
    (rollout.counts_by_status.failed ?? 0) +
    (rollout.counts_by_status.cancelled ?? 0) +
    (rollout.counts_by_status.timed_out ?? 0)

  return (
    <button
      type="button"
      onClick={onOpen}
      title="Rollout 상세 보기"
      style={{
        display: 'grid',
        gap: 3,
        padding: 0,
        border: 0,
        background: 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
        font: 'inherit',
      }}
    >
      <span style={rolloutPillStyle(rollout.status)}>{rolloutStatusLabel(rollout.status)}</span>
      <span style={{ fontSize: 11, color: '#666' }}>
        완료 {succeeded}/{rollout.target_count} · 실패 {failed}
      </span>
    </button>
  )
}

function isRolloutFinal(status: string): boolean {
  return ['completed', 'failed', 'cancelled'].includes(status)
}

function rolloutStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    running_canary: 'Canary 진행 중',
    awaiting_approval: 'Canary 완료',
    running: '본 배포 진행 중',
    completed: '배포 완료',
    failed: '실패',
    cancelled: '취소됨',
  }
  return labels[status] ?? status
}

const th: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 600,
  color: '#555',
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
}
const td: React.CSSProperties = {
  padding: '10px 12px',
  verticalAlign: 'middle',
}
function pill(bg: string): React.CSSProperties {
  return {
    background: bg,
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    color: '#333',
  }
}

function rolloutPillStyle(status: string): React.CSSProperties {
  const bg: Record<string, string> = {
    running_canary: '#fff8e1',
    awaiting_approval: '#e8f5e9',
    running: '#e3f2fd',
    completed: '#e8f5e9',
    failed: '#ffebee',
    cancelled: '#f5f5f7',
  }
  return {
    ...pill(bg[status] ?? '#f5f5f7'),
    display: 'inline-block',
    width: 'fit-content',
  }
}

const secondaryBtnStyle: React.CSSProperties = {
  padding: '7px 12px',
  background: '#f5f5f7',
  color: '#333',
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
}
